import {
  Button,
  Card,
  CardActions,
  CardContent,
  Container,
  Alert,
  Skeleton,
  TextField,
  MenuItem,
  Grid2 as Grid,
} from "@mui/material";
import { gql } from "@apollo/client";
import { useState, useMemo } from "react";
import { isEqual, pick } from "lodash";
import { useCurrentOrg } from "~/providers/GraphqlProvider";
import { getOrgConfigWithDefaults, MobileUnlockMethod } from "@caredna-marlin/utils/organizationTypes";
import Help from "~/components/common/Help";
import { Permission, usePermissionOrRedirect } from "~/hooks/permissions";
import { MobileUnlockMethodEnum, useUpdateOrganizationTimeoutsMutation } from "~/graphql/__generated__/graphql";

// Local type for UI state that allows null for empty form fields
type SecurityConfig = {
  idleSessionTimeoutMinutes: number;
  mobileIdleLockTimeoutMinutes: number;
  mobileIdleSignOutMinutes: number;
  mobileUnlockMethod: MobileUnlockMethod;
};

const unlockMethodOptions: { value: MobileUnlockMethod; label: string }[] = [
  { value: "pin", label: "User PIN" },
  { value: "device", label: "Device Unlock" },
];

const leftCol = { xl: 2, lg: 3, med: 4, xs: 6 }
const rightCol = { xl: 10, lg: 9, med: 8, xs: 6 }

const TabSettings = () => {
  const currentOrg = useCurrentOrg();
  usePermissionOrRedirect(Permission.manageOrgConfig);

  const allowedRanges: Record<string, {min: number, max: number}> = {
    idleSessionTimeoutMinutes: {min: 5, max: 720},
    mobileIdleLockTimeoutMinutes: {min: 1, max: 120},
    mobileIdleSignOutMinutes: {min: 5, max: 10080},
  };

  const formErrorDefaults = {
    idleSessionTimeoutMinutes: false,
    mobileIdleLockTimeoutMinutes: false,
    mobileIdleSignOutMinutes: false,
  };
  const [formErrors, setFormErrors] = useState<Record<string, boolean>>(formErrorDefaults);

  const originalConfig = useMemo(() =>
    pick(
      getOrgConfigWithDefaults(currentOrg?.settings),
      "idleSessionTimeoutMinutes",
      "mobileIdleLockTimeoutMinutes",
      "mobileIdleSignOutMinutes",
      "mobileUnlockMethod",
    ),
    [currentOrg?.settings]
  );

  // Convert to local config type for UI state
  const [localConfig, setLocalConfig] = useState<SecurityConfig>({
    ...originalConfig
  });

  const anyErrors = Object.keys(formErrors).some((key) => formErrors[key])

  const [updateOrganizationSettings, { loading: updateLoading, error }] = useUpdateOrganizationTimeoutsMutation(
    { refetchQueries: ["GetAvailableOrgs"], awaitRefetchQueries: true }
  );

  // Compare with original config, treating null as the default value
  const noLocalChanges = isEqual(localConfig, originalConfig);

  const onSave = async () => updateOrganizationSettings({
    variables: {
      ...localConfig,
      // The generated mutation expects the GraphQL enum; its values match MobileUnlockMethod.
      mobileUnlockMethod: localConfig.mobileUnlockMethod as MobileUnlockMethodEnum,
    },
  });

  const onReset = () => {
    setLocalConfig({
      ...originalConfig,
    });
    setFormErrors(formErrorDefaults);
  };

  const handleWindowChange = (field: string, input: string) => {
    const value = input == "" ? null : parseInt(input);

    if(value === null || !isNaN(value)) {
      setLocalConfig({
        ...localConfig,
        [field]: value,
      });
    }

    const hasError = value === null || isNaN(value) || value < allowedRanges[field].min || value > allowedRanges[field].max;
    setFormErrors({
      ...formErrors,
      [field]: hasError
    })
  };

  if (!currentOrg) {
    return (
      <Container maxWidth={false} sx={{ mt: 4, mb: 4 }}>
        <Skeleton variant="rectangular" width="100%" height={200} />
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to update settings: {error.message}
        </Alert>
      )}

      <Card sx={{ height: "100%", pl: 1 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid size={leftCol}>
              Web Idle Sign Out
              <Help text={`Web app users will be automatically signed out after this period of inactivity (${allowedRanges.idleSessionTimeoutMinutes.min}-${allowedRanges.idleSessionTimeoutMinutes.max} minutes)`} />
            </Grid>
            <Grid size={rightCol}>
              <TextField
                label="Sign out after (minutes)"
                type="number"
                value={localConfig.idleSessionTimeoutMinutes ?? ''}
                onChange={(e) => handleWindowChange("idleSessionTimeoutMinutes", e.target.value)}
                disabled={updateLoading}
                error={formErrors.idleSessionTimeoutMinutes}
                size="small"
                slotProps={{
                  htmlInput: {
                    min: allowedRanges.idleSessionTimeoutMinutes.min,
                    max: allowedRanges.idleSessionTimeoutMinutes.max,
                  }
                }}
                sx={{ minWidth: 180, maxWidth: 300 }}
              />
            </Grid>
            <Grid size={leftCol}>
              Mobile Idle Lock
              <Help text={`The mobile app will lock after this period of inactivity (${allowedRanges.mobileIdleLockTimeoutMinutes.min}-${allowedRanges.mobileIdleLockTimeoutMinutes.max} minutes).`} />
            </Grid>
            <Grid size={rightCol}>
              <TextField
                label="Lock screen after (minutes)"
                type="number"
                value={localConfig.mobileIdleLockTimeoutMinutes ?? ''}
                onChange={(e) => handleWindowChange("mobileIdleLockTimeoutMinutes", e.target.value)}
                disabled={updateLoading}
                error={formErrors.mobileIdleLockTimeoutMinutes}
                size="small"
                slotProps={{
                  htmlInput: {
                    min: allowedRanges.mobileIdleLockTimeoutMinutes.min,
                    max: allowedRanges.mobileIdleLockTimeoutMinutes.max,
                  }
                }}
                sx={{ minWidth: 180, maxWidth: 300 }}
                />
            </Grid>
            <Grid size={leftCol}>
              Mobile Idle Sign Out
              <Help text={`The mobile app will fully sign a user out after this period of inactivity (${allowedRanges.mobileIdleSignOutMinutes.min}-${allowedRanges.mobileIdleSignOutMinutes.max} minutes).`} />
            </Grid>
            <Grid size={rightCol}>
              <TextField
                label="Sign out after (minutes)"
                type="number"
                value={localConfig.mobileIdleSignOutMinutes ?? ''}
                onChange={(e) => handleWindowChange("mobileIdleSignOutMinutes", e.target.value)}
                disabled={updateLoading}
                error={formErrors.mobileIdleSignOutMinutes}
                size="small"
                slotProps={{
                  htmlInput: {
                    min: allowedRanges.mobileIdleSignOutMinutes.min,
                    max: allowedRanges.mobileIdleSignOutMinutes.max,
                  }
                }}
                sx={{ minWidth: 180, maxWidth: 300 }}
                />
            </Grid>
            <Grid size={leftCol}>
              Mobile Unlock Method
              <Help text="Select the method used for authenticated users to unlock the app after idle. Device Unlock should only be used if mobile devices are never shared between users." />
            </Grid>
            <Grid size={rightCol}>
              <TextField
                select
                label="Unlock Method"
                value={localConfig.mobileUnlockMethod}
                onChange={(e) =>
                  setLocalConfig({ ...localConfig, mobileUnlockMethod: e.target.value as MobileUnlockMethod })
                }
                disabled={updateLoading}
                size="small"
                sx={{ minWidth: 180, maxWidth: 300 }}
              >
                {unlockMethodOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </CardContent>

        <CardActions>
          <Button
            variant="contained"
            size="small"
            color="primary"
            disabled={anyErrors || noLocalChanges || updateLoading}
            onClick={onSave}
          >
            {updateLoading ? "Saving..." : "Save"}
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="secondary"
            disabled={noLocalChanges || updateLoading}
            onClick={onReset}
            sx={{ ml: 1 }}
          >
            Reset
          </Button>
        </CardActions>
      </Card>
    </Container>
  );
};

gql(`
  mutation UpdateOrganizationTimeouts(
    $idleSessionTimeoutMinutes: Int!
    $mobileIdleLockTimeoutMinutes: Int!
    $mobileIdleSignOutMinutes: Int!
    $mobileUnlockMethod: MobileUnlockMethodEnum!
  )
  @permission(requires: manageOrgConfig)
  {
    updateOrganizationSettings(
      idleSessionTimeoutMinutes: $idleSessionTimeoutMinutes
      mobileIdleLockTimeoutMinutes: $mobileIdleLockTimeoutMinutes
      mobileIdleSignOutMinutes: $mobileIdleSignOutMinutes
      mobileUnlockMethod: $mobileUnlockMethod
    ) { id }
  }
`);

export default TabSettings;
